"use client";

import { useState } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import EditableElement from "@/components/customize/EditableElement";

export default function Gifts({
  heading, subtitle, transfers = [], gifts = [], __isMobile = false,
  __isEditable = false,
  __sectionId,
  __onFieldEdit,
  __onFieldChange,
  __onFileSelected,
  __focusFieldName,
}) {
  const [isTransfer, setIsTransfer] = useState(true);

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const variants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 20 },
  };
  const titleClass = __isMobile ? "text-3xl" : "text-3xl md:text-4xl";

  return (
    <div className="max-w-6xl bg-white text-black mx-auto py-6">
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeInUp}
      >
        {heading && (
          <EditableElement
            type="text"
            value={heading}
            onChange={(val) => __onFieldChange?.(__sectionId, "heading", val)}
            isEditable={__isEditable}
            fieldName="heading"
            onEditStart={() => __onFieldEdit?.(__sectionId, "heading")}
            isActive={__focusFieldName === "heading"}
            isMobile={__isMobile}
          >
            <h1 className={`block px-6 md:px-8 text-left font-semibold font-cormorant ${titleClass}`}>
              {heading}
            </h1>
          </EditableElement>
        )}
        {subtitle && (
          <EditableElement
            type="text"
            value={subtitle}
            onChange={(val) => __onFieldChange?.(__sectionId, "subtitle", val)}
            isEditable={__isEditable}
            fieldName="subtitle"
            onEditStart={() => __onFieldEdit?.(__sectionId, "subtitle")}
            isActive={__focusFieldName === "subtitle"}
            isMobile={__isMobile}
          >
            <p className="block px-6 md:px-8 text-left mt-2 text-xs font-sans">{subtitle}</p>
          </EditableElement>
        )}

        {/* Tabs */}
        <div className="flex items-center justify-center space-x-4 mt-4">
          <button
            onClick={() => setIsTransfer(true)}
            className={`${
              isTransfer ? "bg-black text-white" : "hover:bg-black/25"
            } cursor-pointer transition-all duration-500 border  py-2 px-4 flex items-center space-x-2`}
          >
            <span>Transfer</span>
          </button>

          <button
            onClick={() => setIsTransfer(false)}
            className={`${
              !isTransfer ? "bg-black text-white" : "hover:bg-black/25"
            } cursor-pointer transition-all duration-500 border  py-2 px-4 flex items-center space-x-2`}
          >
            <span>Gift Registry</span>
          </button>
        </div>

        {/* Content */}
        <div className="relative min-h-75 mt-4">
          <AnimatePresence mode="wait">
            {isTransfer ? (
              <motion.div
                key="transfer"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={variants}
                transition={{ duration: 0.4 }}
                className="absolute inset-0 px-6 md:px-8"
                style={{ willChange: "opacity, transform" }}
              >
                {transfers.map((data, i) => (
                  <div
                    key={i}
                    className="mb-2 p-4 shadow border bg-white/20 backdrop-blur border-gray-300 flex items-center justify-between"
                  >
                    <div className="flex flex-col items-start">
                      <EditableElement
                        type="text"
                        value={data.name}
                        onChange={(val) => __onFieldChange?.(__sectionId, `transfers[${i}].name`, val)}
                        isEditable={__isEditable}
                        fieldName={`transfers[${i}].name`}
                        onEditStart={() => __onFieldEdit?.(__sectionId, `transfers[${i}].name`)}
                        isActive={__focusFieldName === `transfers[${i}].name`}
                        isMobile={__isMobile}
                        className="block mb-1"
                      >
                        <p className="font-medium">A/n {data.name}</p>
                      </EditableElement>
                      <EditableElement
                        type="text"
                        value={data.bank}
                        onChange={(val) => __onFieldChange?.(__sectionId, `transfers[${i}].bank`, val)}
                        isEditable={__isEditable}
                        fieldName={`transfers[${i}].bank`}
                        onEditStart={() => __onFieldEdit?.(__sectionId, `transfers[${i}].bank`)}
                        isActive={__focusFieldName === `transfers[${i}].bank`}
                        isMobile={__isMobile}
                        className="block mb-1"
                      >
                        <p className="leading-none">{data.bank}</p>
                      </EditableElement>
                      <EditableElement
                        type="text"
                        value={data.no}
                        onChange={(val) => __onFieldChange?.(__sectionId, `transfers[${i}].no`, val)}
                        isEditable={__isEditable}
                        fieldName={`transfers[${i}].no`}
                        onEditStart={() => __onFieldEdit?.(__sectionId, `transfers[${i}].no`)}
                        isActive={__focusFieldName === `transfers[${i}].no`}
                        isMobile={__isMobile}
                        className="block"
                      >
                        <p className="leading-none">{data.no}</p>
                      </EditableElement>
                    </div>
                    <div>
                      <button className="border border-white/40 px-3 py-1.5 text-sm whitespace-nowrap rounded-lg leading-none hover:bg-black/25 transition-all duration-500 cursor-pointer">
                        Copy
                      </button>
                    </div>
                  </div>
                ))}
              </motion.div>
            ) : (
              <motion.div
                key="gift-registry"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={variants}
                transition={{ duration: 0.4 }}
                className="absolute inset-0 overflow-y-auto h-75 px-6 md:px-8"
                style={{ willChange: "opacity, transform" }}
              >
                {gifts.map((item, i) => (
                  <div
                    key={i}
                    className="mb-2 p-4 shadow border bg-white/20 backdrop-blur border-gray-300 flex items-center justify-between"
                  >
                    <div className="flex space-x-2 items-center">
                      <EditableElement
                        type="image"
                        value={item.image}
                        onChange={(val) => __onFieldChange?.(__sectionId, `gifts[${i}].image`, val)}
                        isEditable={__isEditable}
                        fieldName={`gifts[${i}].image`}
                        onFileSelected={(file, url) => __onFileSelected?.(`defaultSections.${__sectionId}.props.gifts[${i}].image`, file, url)}
                      >
                        {item.image && (
                          <div className="w-14 h-14 min-w-fit">
                            <Image
                              className="w-14 h-14 object-cover rounded"
                              width={150}
                              height={150}
                              src={item.image}
                              alt={item.title}
                              loading="lazy"
                            />
                          </div>
                        )}
                      </EditableElement>
                      <div>
                        <EditableElement
                          type="text"
                          value={item.title}
                          onChange={(val) => __onFieldChange?.(__sectionId, `gifts[${i}].title`, val)}
                          isEditable={__isEditable}
                          fieldName={`gifts[${i}].title`}
                          onEditStart={() => __onFieldEdit?.(__sectionId, `gifts[${i}].title`)}
                          isActive={__focusFieldName === `gifts[${i}].title`}
                          isMobile={__isMobile}
                        >
                          <p className="font-medium text-sm line-clamp-1">
                            {item.title}
                          </p>
                        </EditableElement>
                        <p className="text-sm mt-1">Jumlah: 
                          <EditableElement
                            type="text"
                            value={item.amount}
                            onChange={(val) => __onFieldChange?.(__sectionId, `gifts[${i}].amount`, val)}
                            isEditable={__isEditable}
                            fieldName={`gifts[${i}].amount`}
                            onEditStart={() => __onFieldEdit?.(__sectionId, `gifts[${i}].amount`)}
                            isActive={__focusFieldName === `gifts[${i}].amount`}
                            isMobile={__isMobile}
                            className="inline-block ml-1"
                          >
                            <span>{item.amount}</span>
                          </EditableElement>
                        </p>
                        <EditableElement
                          type="text"
                          value={item.price}
                          onChange={(val) => __onFieldChange?.(__sectionId, `gifts[${i}].price`, val)}
                          isEditable={__isEditable}
                          fieldName={`gifts[${i}].price`}
                          onEditStart={() => __onFieldEdit?.(__sectionId, `gifts[${i}].price`)}
                          isActive={__focusFieldName === `gifts[${i}].price`}
                          isMobile={__isMobile}
                        >
                          <p className="leading-none text-sm mt-1">{item.price}</p>
                        </EditableElement>
                      </div>
                    </div>
                    <div>
                      <button className="border border-white/40 px-3 py-1.5 text-sm whitespace-nowrap rounded-lg leading-none hover:bg-black/25 transition-all duration-500 cursor-pointer">
                        Beri Hadiah
                      </button>
                    </div>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
